import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useAppSelector } from "../../hooks/redux/useRedux";
import { selectCheckPayment } from "../../redux/slices/paymentSlice/PaymentSlice";
import {
  HiOutlineMicrophone,
  HiOutlineStop,
  HiOutlinePlay,
  HiOutlinePause,
  HiOutlineLockClosed,
} from "react-icons/hi";
import toast from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import { REST_API_URL } from "../../api/config";

const BASE_URL = REST_API_URL;

const TEXT_URL = `${BASE_URL}/voicecloning/voice-cloning-text/`;
const VOICE_URL = `${BASE_URL}/voicecloning/voice/`;
const PAYMENT_CHECK_URL = `${BASE_URL}/auth/payment-check/`;
const SOCIAL_PROFILE_URL = `${BASE_URL}/auth/social-profile-view/`;

const getUserTimeZone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const buildHeaders = (token, additionalHeaders = {}) => {
  const headers = {
    "Content-Type": "application/json",
    ...additionalHeaders,
  };
  
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  
  const timezone = getUserTimeZone();
  if (timezone) {
    headers["User-Timezone"] = timezone;
  }
  
  return headers;
};

function VoiceMessagePlayer({ audioSrc }) {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const waves = useMemo(
    () => Array.from({ length: 48 }, () => Math.random() * 0.6 + 0.4),
    []
  );

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onLoaded = () => setDuration(audio.duration || 0);
    const onTime = () => setCurrentTime(audio.currentTime || 0);
    const onEnd = () => setIsPlaying(false);

    audio.addEventListener("loadedmetadata", onLoaded);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("ended", onEnd);

    return () => {
      audio.removeEventListener("loadedmetadata", onLoaded);
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("ended", onEnd);
    };
  }, []);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (audio.paused) {
      audio.play();
      setIsPlaying(true);
    } else {
      audio.pause();
      setIsPlaying(false);
    }
  };

  const handleSeek = (e) => {
    if (!duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    audioRef.current.currentTime = Math.max(
      0,
      Math.min(percent * duration, duration)
    );
  };

  const progress = duration ? currentTime / duration : 0;

  const formatLeft = (t) => {
    const left = Math.max(duration - t, 0);
    const m = Math.floor(left / 60);
    const s = Math.floor(left % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  return (
    <div className="flex items-center gap-3 px-4 py-3 rounded-2xl justify-center">
      <audio ref={audioRef} src={audioSrc} preload="metadata" />

      <div className="flex gap-3">
        <motion.button
          whileTap={{ scale: 0.9 }}
          onClick={togglePlay}
          className={`w-10 h-10 rounded-full flex items-center justify-center ${
            isPlaying ? "bg-red-100 text-red-500" : "bg-blue-100 text-blue-500"
          }`}
        >
          <AnimatePresence mode="wait">
            {isPlaying ? (
              <motion.div
                key="pause"
                initial={{ scale: 0.6, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.6, opacity: 0 }}
              >
                <HiOutlinePause className="w-5 h-5" />
              </motion.div>
            ) : (
              <motion.div
                key="play"
                initial={{ scale: 0.6, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.6, opacity: 0 }}
              >
                <HiOutlinePlay className="w-5 h-5 ml-[1px]" />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.button>

        <div
          onClick={handleSeek}
          className="flex-1 flex items-center gap-[3px] h-10 cursor-pointer"
        >
          {waves.map((h, i) => {
            const played = i / waves.length < progress;

            return (
              <motion.div
                key={i}
                className={`w-[3px] rounded-full ${
                  played ? "bg-blue-500" : "bg-gray-300"
                }`}
                animate={isPlaying ? { scaleY: [1, 1.4, 1] } : { scaleY: 1 }}
                transition={{
                  duration: 0.6,
                  repeat: isPlaying ? Infinity : 0,
                  delay: i * 0.02,
                }}
                style={{ height: `${h * 22}px` }}
              />
            );
          })}
        </div>
      </div>

      <div className="text-xs font-medium text-gray-600 w-[40px] text-right">
        {formatLeft(currentTime)}
      </div>
    </div>
  );
}

export default function VoiceCloning() {
  const [text, setText] = useState("");
  const [voiceUrl, setVoiceUrl] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [recordedAudio, setRecordedAudio] = useState(null);
  const [volume, setVolume] = useState(0);
  const [showPreview, setShowPreview] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasExistingVoice, setHasExistingVoice] = useState(false);
  const paymentCheck = useAppSelector(selectCheckPayment);
  const [socialProfile, setSocialProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessGranted, setAccessGranted] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const analyserRef = useRef(null);
  const dataArrayRef = useRef(null);
  const animationRef = useRef(null);
  const timerRef = useRef(null);

  const getToken = useCallback(() => {
    return localStorage.getItem("accessToken") || sessionStorage.getItem("accessToken");
  }, []);

  useEffect(() => {
    const checkAccess = async () => {
      setLoading(true);
      const token = getToken();
      
      if (!token) {
        setLoading(false);
        return;
      }

      const headers = buildHeaders(token);
      
      try {
        if (paymentCheck !== null) {
          if (paymentCheck.has_active_subscription) {
            setAccessGranted(true);
            loadVoiceData(headers);
            return;
          }
        } else {
          const paymentResponse = await fetch(PAYMENT_CHECK_URL, { headers });
          if (paymentResponse.ok) {
            const paymentData = await paymentResponse.json();
            if (paymentData.has_active_subscription) {
              setAccessGranted(true);
              loadVoiceData(headers);
              return;
            }
          }
        }

        const socialResponse = await fetch(SOCIAL_PROFILE_URL, { headers });
        if (socialResponse.ok) {
          const socialData = await socialResponse.json();
          setSocialProfile(socialData);
          
          if (socialData.instagram || socialData.whatsapp) {
            setAccessGranted(true);
            loadVoiceData(headers);
            return;
          }
        }

        setAccessGranted(false);
      } catch (error) {
        console.error("Access check error:", error);
        toast.error("Failed to check access permissions");
      } finally {
        setLoading(false);
      }
    };

    const loadVoiceData = async (headers) => {
      try {
        const textResponse = await fetch(TEXT_URL, { headers });
        if (textResponse.ok) {
          const textData = await textResponse.json();
          setText(textData.text || "");
        }

        const voiceResponse = await fetch(VOICE_URL, { headers });
        if (voiceResponse.ok) {
          const voiceData = await voiceResponse.json();
          if (voiceData.voice_url) {
            setVoiceUrl(voiceData.voice_url);
            setHasExistingVoice(true);
          }
        } else if (voiceResponse.status !== 404) {
          console.error("Voice check error:", voiceResponse.status);
        }
      } catch (error) {
        console.error("Voice data load error:", error);
      }
    };

    checkAccess();

    return () => {
      stopAnimation();
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [getToken]);

  const animateVolume = () => {
    const analyser = analyserRef.current;
    const dataArray = dataArrayRef.current;
    if (!analyser || !dataArray) return;

    const animate = () => {
      analyser.getByteFrequencyData(dataArray);
      const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
      setVolume(avg);
      animationRef.current = requestAnimationFrame(animate);
    };
    animate();
  };

  const stopAnimation = () => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
  };

  const startRecording = async () => {
    if (!accessGranted) {
      toast.error("Access denied. Please check requirements.");
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: { 
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        } 
      });

      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      source.connect(analyser);

      analyserRef.current = analyser;
      dataArrayRef.current = dataArray;
      animateVolume();

      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: "audio/webm",
        });
        const audioUrl = URL.createObjectURL(audioBlob);
        setRecordedAudio(audioUrl);
        setShowPreview(true);
        stopAnimation();
        stream.getTracks().forEach((t) => t.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);

      toast("🎙️ Recording...");
    } catch (error) {
      console.error("Recording error:", error);
      toast.error("Microphone permission denied");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop();
      clearInterval(timerRef.current);
      setIsRecording(false);
      toast.success("Recording saved");
    }
  };

  const saveRecording = async () => {
    if (!recordedAudio) return;
    
    setIsSaving(true);
    
    try {
      const token = getToken();
      
      const response = await fetch(recordedAudio);
      const blob = await response.blob();
      const file = new File([blob], "voice_sample.webm", { type: "audio/webm" });
      
      const formData = new FormData();
      formData.append("voice_file", file);
      
      if (hasExistingVoice) {
        try {
          const deleteHeaders = buildHeaders(token, {
            "Content-Type": undefined
          });
          
          const deleteResponse = await fetch(VOICE_URL, {
            method: "DELETE",
            headers: deleteHeaders
          });
          
          if (!deleteResponse.ok && deleteResponse.status !== 204) {
            throw new Error("Failed to delete existing voice");
          }
        } catch (deleteError) {
          console.error("Delete error:", deleteError);
        }
      }
      
      const putHeaders = buildHeaders(token, {
        "Content-Type": undefined
      });
      
      const putResponse = await fetch(VOICE_URL, {
        method: "PUT",
        headers: putHeaders,
        body: formData
      });
      
      if (putResponse.ok) {
        const getHeaders = buildHeaders(token);
        
        const getResponse = await fetch(VOICE_URL, {
          headers: getHeaders
        });
        
        if (getResponse.ok) {
          const voiceData = await getResponse.json();
          setVoiceUrl(voiceData.voice_url || recordedAudio);
          setHasExistingVoice(true);
          
          toast.success("Voice sample saved successfully!");
        } else {
          setVoiceUrl(recordedAudio);
          toast.success("Voice saved, but failed to get new URL");
        }
      } else {
        throw new Error(`PUT failed with status ${putResponse.status}`);
      }
      
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to save voice sample");
      setVoiceUrl(recordedAudio);
    } finally {
      setIsSaving(false);
      setShowPreview(false);
      
      if (recordedAudio) {
        URL.revokeObjectURL(recordedAudio);
      }
      setRecordedAudio(null);
    }
  };

  const discardRecording = () => {
    if (recordedAudio) {
      URL.revokeObjectURL(recordedAudio);
    }
    setShowPreview(false);
    setRecordedAudio(null);
    toast("Recording discarded");
  };

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m.toString().padStart(2, "0")}:${sec
      .toString()
      .padStart(2, "0")}`;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-8 space-y-6">
        <div className="flex flex-col items-center justify-center h-64">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="mt-4 text-gray-600">Checking access permissions...</p>
        </div>
      </div>
    );
  }

  if (!accessGranted) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-8 space-y-6">
        <div className="flex flex-col items-center text-center">
          <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mb-4">
            <HiOutlineLockClosed className="w-10 h-10 text-yellow-600" />
          </div>
          
          <h2 className="text-2xl font-bold text-gray-800">Access Required</h2>
          <p className="text-gray-600 mt-2">
            To use Voice Cloning, you need to meet one of these requirements:
          </p>
          
          <div className="mt-6 space-y-4 text-left w-full max-w-md">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-blue-600 font-bold">1</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-700">Active Subscription</h3>
                <p className="text-gray-600 text-sm mt-1">
                  Subscribe to a paid plan to unlock all features
                </p>
                {paymentCheck && (
                  <p className={`text-sm mt-1 ${paymentCheck.has_active_subscription ? 'text-green-600' : 'text-red-600'}`}>
                    Status: {paymentCheck.has_active_subscription ? 'Active ✓' : 'Inactive ✗'}
                  </p>
                )}
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-blue-600 font-bold">2</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-700">Social Connection</h3>
                <p className="text-gray-600 text-sm mt-1">
                  Connect your Instagram or WhatsApp account
                </p>
                {socialProfile && (
                  <div className="mt-2 space-y-1">
                    <p className={`text-sm ${socialProfile.instagram ? 'text-green-600' : 'text-red-600'}`}>
                      Instagram: {socialProfile.instagram ? 'Connected ✓' : 'Not connected ✗'}
                    </p>
                    <p className={`text-sm ${socialProfile.whatsapp ? 'text-green-600' : 'text-red-600'}`}>
                      WhatsApp: {socialProfile.whatsapp ? 'Connected ✓' : 'Not connected ✗'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Voice Cloning</h2>
          <p className="text-gray-600 mt-1">
            Read the text below while recording
          </p>
        </div>
        {paymentCheck?.has_active_subscription && (
          <span className="text-xs bg-green-100 text-green-800 px-3 py-1 rounded-full">
            Premium User
          </span>
        )}
      </div>

      <div>
        <h3 className="font-semibold text-gray-700 mb-2">Text to Read</h3>
        <div className="bg-gray-50 border rounded-xl pl-4 max-h-[auto] overflow-y-auto">
          <p className="text-gray-700 whitespace-pre-line">
            {text || "Loading..."}
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex flex-col items-center gap-4">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={isRecording ? stopRecording : startRecording}
            className={`w-20 h-20 rounded-full flex items-center justify-center shadow-lg hover:shadow-xl ${
              isRecording 
                ? "bg-red-500 hover:bg-red-600" 
                : "bg-blue-500 hover:bg-blue-600"
            } text-white`}
          >
            {isRecording ? (
              <HiOutlineStop className="w-8 h-8" />
            ) : (
              <HiOutlineMicrophone className="w-8 h-8" />
            )}
          </motion.button>
          <p className="text-gray-500">
            {isRecording ? "Recording..." : "Click to start recording"}
          </p>
        </div>
      </div>

      <AnimatePresence>
        {showPreview && recordedAudio && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="space-y-3"
          >
            <div className="border border-blue-200 rounded-2xl p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                <span className="text-sm font-medium text-gray-700">
                  New Recording ({formatTime(seconds)})
                </span>
              </div>

              <VoiceMessagePlayer audioSrc={recordedAudio} />

              <div className="flex gap-2 mt-3">
                <button
                  onClick={saveRecording}
                  disabled={isSaving}
                  className={`flex-1 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center justify-center gap-2 ${
                    isSaving ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  {isSaving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Saving...
                    </>
                  ) : (
                    "Save"
                  )}
                </button>
                <button
                  onClick={discardRecording}
                  disabled={isSaving}
                  className={`flex-1 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 ${
                    isSaving ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  Discard
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {voiceUrl && !showPreview && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-gray-700">Your Voice Sample</h3>
            {hasExistingVoice && (
              <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                Saved on server
              </span>
            )}
          </div>
          <VoiceMessagePlayer audioSrc={voiceUrl} />
        </div>
      )}

      <AnimatePresence>
        {isRecording && (
          <motion.div
            className="fixed inset-0 bg-black/40 flex flex-col justify-center items-center z-50"
            style={{ marginTop: "0px" }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="w-32 h-32 rounded-full bg-blue-500 flex justify-center items-center cursor-pointer"
              onClick={stopRecording}
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ repeat: Infinity, duration: 1 }}
              style={{
                boxShadow: `0 0 ${20 + volume / 5}px rgba(59, 130, 246, 0.8)`,
              }}
            >
              <HiOutlineStop className="w-12 h-12 text-white" />
            </motion.div>
            <p className="mt-6 text-white text-lg font-medium">
              Recording... ({formatTime(seconds)})
            </p>
            <p className="mt-2 text-gray-300">Click to stop</p>

            <div className="mt-4 flex items-center gap-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <motion.div
                  key={i}
                  className="w-1 h-6 bg-white rounded-full"
                  animate={{ height: [10, 20 + (volume / 10) * i, 10] }}
                  transition={{
                    repeat: Infinity,
                    duration: 0.5,
                    delay: i * 0.1,
                  }}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}